import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { ensurePushSubscription, currentNotifyPermission, supportsPush } from '../utils/webpush';
import { isNativeApp } from '../utils/installState';

// 进入页面后，还没决定是否开启通知的用户直接弹窗（原来只有工作台顶部一条横幅，容易被忽略）。
// 按钮和横幅一样：点“开启通知”才调系统授权框（iOS 要求必须由点击触发）。
// 一次打开页面只弹一次；已开启 / 已拒绝的不弹（已拒绝的仍看工作台的红色提示横幅）。
// APK 里权限未决定时由工作台自动弹系统框，这里不重复弹。
export default function NotifyPermissionPrompt() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!user || user.role === 'admin' || shownRef.current) return;
    const timer = setTimeout(() => {
      if (shownRef.current) return;
      if (!supportsPush || isNativeApp() || currentNotifyPermission() !== 'default') return;
      shownRef.current = true;
      setShow(true);
    }, 1200);   // 等页面先渲染稳定再弹
    return () => clearTimeout(timer);
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // 测试用：在浏览器控制台执行  window.dispatchEvent(new Event('show-notify-prompt'))  可强制弹出（不看权限 / 角色）
  useEffect(() => {
    const force = () => setShow(true);
    window.addEventListener('show-notify-prompt', force);
    return () => window.removeEventListener('show-notify-prompt', force);
  }, []);

  if (!show) return null;

  // 必须直接在点击里调用（ensurePushSubscription 第一步就是 Notification.requestPermission）
  const enable = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await ensurePushSubscription();
    setBusy(false);
    setShow(false);
    try { window.dispatchEvent(new Event('notify-perm-changed')); } catch { /* ignore */ }   // 让工作台横幅同步刷新
    if (ok) showToast('已开启消息通知', 'success');
    else if (currentNotifyPermission() === 'denied') showToast('通知权限已被拒绝，请在系统设置中开启', 'error');
  };

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label="开启消息通知"
      style={{
        position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 28px',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 320, background: 'var(--c-surface)', borderRadius: 20,
        padding: '24px 22px 16px', textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: 'var(--c-primary-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
        }}>
          <Bell size={28} color="var(--c-primary)" />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--c-text)' }}>开启消息通知</div>
        <div style={{ fontSize: 14, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginTop: 6 }}>
          开启通知后，即使把网页切到后台也能收到新消息提醒
        </div>
        <button className="btn btn-primary" disabled={busy} onClick={enable}
          style={{ width: '100%', height: 44, marginTop: 18, fontSize: 16 }}>
          开启通知
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => setShow(false)}
          style={{ width: '100%', height: 40, marginTop: 4, fontSize: 14 }}>
          稍后再说
        </button>
      </div>
    </div>,
    document.body
  );
}
